import { supabase } from "@/integrations/supabase/client";
import { canonicalText } from "@/lib/pharmacology/substanceCatalog";
import { recordMedicationRegimenEvent } from "@/lib/clinical/medicationRegimenPersistence";

export type PatientMedicationInput = {
  owner_id: string;
  patient_id: string;
  substance_id?: string | null;
  formulation_id?: string | null;
  brand_name?: string | null;
  free_text_name?: string | null;
  current_dose?: number | null;
  dose_unit?: string | null;
  frequency?: string | null;
  usual_time?: string | null;
  start_date?: string | null;
  status?: string;
  expected_benefit_0_100?: number | null;
  expected_cost_0_100?: number | null;
  individual_sensitivity_0_100?: number | null;
  individual_tolerance_0_100?: number | null;
  redose_risk_individual_0_100?: number | null;
  psychosis_activation_risk_individual_0_100?: number | null;
  clinical_rationale?: string | null;
  monitoring_plan?: string | null;
  warning_signs?: string | null;
  patient_notes?: string | null;
  physician_notes?: string | null;
  indication?: string | null;
  diagnostic_target?: string | null;
  target_symptoms?: string[] | null;
  expected_improvements?: string[] | null;
  potential_worsening?: string[] | null;
  stop_or_reduce_criteria?: string | null;
  discontinuation_reason?: string | null;
  end_date?: string | null;
  sedation_sensitivity_0_100?: number | null;
  insomnia_sensitivity_0_100?: number | null;
  autonomic_sensitivity_0_100?: number | null;
  abuse_risk_individual_0_100?: number | null;
  withdrawal_risk_individual_0_100?: number | null;
  individual_carry_over?: number | null;
};

function identityOf(row: any) {
  return {
    patient: String(row?.patient_id ?? ""),
    substance: String(row?.substance_id ?? ""),
    formulation: String(row?.formulation_id ?? ""),
    name: canonicalText(row?.brand_name ?? row?.free_text_name ?? ""),
  };
}

export function sameMedicationIdentity(a: any, b: any) {
  const left = identityOf(a);
  const right = identityOf(b);
  if (!left.patient || left.patient !== right.patient) return false;
  if (left.substance && right.substance) {
    if (left.substance !== right.substance) return false;
    if (left.formulation && right.formulation) return left.formulation === right.formulation;
    return true;
  }
  return !!left.name && left.name === right.name;
}

export async function savePatientMedication(input: PatientMedicationInput, medicationId?: string | null) {
  if (!input.owner_id) throw new Error("owner_id ausente. Faça login novamente.");
  if (!input.patient_id) throw new Error("Selecione um paciente.");
  if (!input.substance_id && !String(input.brand_name ?? input.free_text_name ?? "").trim()) {
    throw new Error("Informe uma substância canônica ou um nome de tratamento.");
  }

  const payload = {
    ...input,
    brand_name: input.brand_name?.trim() || null,
    free_text_name: input.free_text_name?.trim() || input.brand_name?.trim() || null,
    status: input.status ?? "ativo",
    usual_time: input.usual_time || null,
  };

  if (medicationId) {
    const { data: previous } = await (supabase as any)
      .from("patient_medications")
      .select("*")
      .eq("id", medicationId)
      .eq("owner_id", input.owner_id)
      .maybeSingle();

    const { data, error } = await (supabase as any)
      .from("patient_medications")
      .update(payload)
      .eq("id", medicationId)
      .eq("owner_id", input.owner_id)
      .select("*")
      .single();
    if (error) {
      console.error("[savePatientMedication] update failed", { error, medicationId, patient_id: input.patient_id });
      throw new Error(`Falha ao atualizar tratamento: ${error.message}`);
    }
    try {
      const doseChanged = Number(data?.current_dose ?? NaN) !== Number(previous?.current_dose ?? NaN) || String(data?.dose_unit ?? "") !== String(previous?.dose_unit ?? "") || String(data?.frequency ?? "") !== String(previous?.frequency ?? "") || String(data?.usual_time ?? "") !== String(previous?.usual_time ?? "") || String(data?.status ?? "") !== String(previous?.status ?? "");
      await recordMedicationRegimenEvent({
        owner_id: input.owner_id,
        patient_id: data.patient_id,
        patient_medication_id: data.id,
        event_type: doseChanged ? "ajuste_dose" : "observacao",
        previous_dose: previous?.current_dose ?? null,
        new_dose: data.current_dose,
        dose_unit: data.dose_unit,
        frequency: data.frequency,
        usual_time: data.usual_time,
        status_after: data.status,
        clinician_assessment: "Atualização do cadastro terapêutico.",
        source: "cadastro de tratamento",
        evidence_context: "registro clínico individual",
      });
    } catch (eventError) {
      console.warn("[savePatientMedication] regimen event mirror failed", eventError);
    }
    return { row: data, created: false, consolidated: false };
  }

  const { data: existingRows = [], error: readError } = await (supabase as any)
    .from("patient_medications")
    .select("*")
    .eq("owner_id", input.owner_id)
    .eq("patient_id", input.patient_id);
  if (readError) throw new Error(`Falha ao verificar tratamentos existentes: ${readError.message}`);

  const existing = (existingRows ?? []).find((row: any) => sameMedicationIdentity(row, payload));
  if (existing) {
    const { data, error } = await (supabase as any)
      .from("patient_medications")
      .update(payload)
      .eq("id", existing.id)
      .select("*")
      .single();
    if (error) {
      console.error("[savePatientMedication] consolidate failed", { error, existingId: existing.id, patient_id: input.patient_id });
      throw new Error(`Falha ao consolidar tratamento já existente: ${error.message}`);
    }
    try {
      await recordMedicationRegimenEvent({
        owner_id: input.owner_id,
        patient_id: data.patient_id,
        patient_medication_id: data.id,
        event_type: "observacao",
        new_dose: data.current_dose,
        dose_unit: data.dose_unit,
        frequency: data.frequency,
        usual_time: data.usual_time,
        status_after: data.status,
        clinician_assessment: "Tratamento consolidado no cadastro longitudinal.",
        source: "cadastro de tratamento",
        evidence_context: "registro clínico individual",
      });
    } catch (eventError) {
      console.warn("[savePatientMedication] regimen event mirror failed", eventError);
    }
    return { row: data, created: false, consolidated: true };
  }

  const { data, error } = await (supabase as any).from("patient_medications").insert(payload).select("*").single();
  if (error) {
    console.error("[savePatientMedication] insert failed", { error, patient_id: input.patient_id, substance_id: input.substance_id });
    throw new Error(`Falha ao salvar tratamento: ${error.message}`);
  }
  try {
    await recordMedicationRegimenEvent({
      owner_id: input.owner_id,
      patient_id: data.patient_id,
      patient_medication_id: data.id,
      event_type: "inicio",
      new_dose: data.current_dose,
      dose_unit: data.dose_unit,
      frequency: data.frequency,
      usual_time: data.usual_time,
      status_after: data.status,
      target_update: data.indication ?? data.diagnostic_target ?? null,
      clinician_assessment: "Tratamento adicionado ao esquema longitudinal.",
      source: "cadastro de tratamento",
      evidence_context: "registro clínico individual",
    });
  } catch (eventError) {
    console.warn("[savePatientMedication] regimen event mirror failed", eventError);
  }
  return { row: data, created: true, consolidated: false };
}
